// Shared note drawer — a short panel from the right, ~10 table rows tall.
// Each saved note is stamped with who wrote it and when (clinic clock).
// Task notes land on the task instructions page; people notes land on the
// employee profile. This panel is only the doorway.
import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { ApiError } from '../lib/apiClient';
import { formatNoteStamp } from '../lib/clinicDay';

export interface NoteFeedItem {
  id: number;
  body: string;
  author?: string | null;
  at: string;
}

export function NoteMark(
  { hasNote, label, testId, onOpen }:
  { hasNote: boolean; label: string; testId: string; onOpen: () => void },
): JSX.Element {
  return (
    <button
      type="button"
      class={hasNote ? 'note-mark has-note' : 'btn'}
      data-testid={testId}
      aria-label={hasNote ? `Open notes for ${label}` : `Add a note for ${label}`}
      title={hasNote ? 'Notes' : 'Add a note'}
      onClick={(e: MouseEvent) => { e.stopPropagation(); onOpen(); }}
    >
      {hasNote ? <span class="sr-only">Notes</span> : 'Note'}
    </button>
  );
}

export function NotePanel(
  { title, notes, loading, onSave, onClose, onDelete }:
  {
    title: string;
    notes: NoteFeedItem[];
    loading?: boolean;
    onSave: (body: string) => Promise<void>;
    onClose: () => void;
    onDelete?: (id: number) => Promise<void>;
  },
): JSX.Element {
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function save(): Promise<void> {
    const text = body.trim();
    if (!text) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(text);
      setBody('');
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: number): Promise<void> {
    if (!onDelete) return;
    setDeleting(true);
    setError(null);
    try {
      await onDelete(id);
      setConfirmId(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div class="note-drawer-root" data-testid="task-note-drawer">
      <button type="button" class="note-drawer-backdrop" aria-label="Close notes" onClick={onClose} />
      <aside class="note-drawer" role="dialog" aria-labelledby="task-note-drawer-title">
        <div class="note-drawer-head">
          <button
            type="button"
            class="note-drawer-close"
            aria-label="Close notes"
            data-testid="task-note-drawer-close"
            onClick={onClose}
          >
            ×
          </button>
          <h3 id="task-note-drawer-title" data-testid="task-note-drawer-title">{title}</h3>
        </div>

        <div class="note-drawer-compose">
          <textarea
            class="notes-entry"
            rows={8}
            aria-label={`Note for ${title}`}
            placeholder="Write a note."
            value={body}
            onInput={(e: Event) => setBody((e.target as HTMLTextAreaElement).value)}
          />
          <button
            type="button"
            class="btn primary"
            data-testid="task-note-drawer-save"
            onClick={() => void save()}
            disabled={saving || !body.trim()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          {error ? <small class="bad" role="alert">{error}</small> : null}
        </div>

        <div class="note-drawer-feed" data-testid="task-note-feed">
          {loading ? (
            <p class="muted">Loading notes…</p>
          ) : notes.length === 0 ? (
            <p class="muted">No notes yet.</p>
          ) : (
            notes.map((n) => (
              <article key={n.id} class="note-drawer-item" data-testid={`task-note-feed-${n.id}`}>
                <div class="section-head-row">
                  <div class="note-drawer-meta">{formatNoteStamp(n.author, n.at)}</div>
                  {onDelete ? (
                    <div class="section-head-actions">
                      {confirmId === n.id ? (
                        <span data-testid={`task-note-feed-${n.id}-confirm`}>
                          Delete?{' '}
                          <button
                            type="button"
                            class="btn"
                            disabled={deleting}
                            onClick={() => void remove(n.id)}
                          >
                            {deleting ? 'Deleting…' : 'Yes'}
                          </button>{' '}
                          <button
                            type="button"
                            class="btn"
                            disabled={deleting}
                            onClick={() => setConfirmId(null)}
                          >
                            Cancel
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          class="btn"
                          data-testid={`task-note-feed-${n.id}-delete`}
                          onClick={() => setConfirmId(n.id)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  ) : null}
                </div>
                <p class="instructions-text">{n.body}</p>
              </article>
            ))
          )}
        </div>
      </aside>
    </div>
  );
}
